import {classes, style} from './animated-auto-sized-element.st.css';
import React, {useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {DomElementResolver, useDynamicRef, useElementLayoutBinding, useRenderState} from "@wix/devzai-utils-react";
import {assertNotNullable, referencesEqualityComparer} from "@wix/devzai-utils-common";

export const AnimatedAutoSizedElement = React.memo(function AnimatedAutoSizedElement (props: AnimatedAutoSizedElement.Props) {
    const {
        children,
        durationMilliseconds = 200,

        className,
        ...htmlAttributes
    } = props;

    const rootRef = useRef<HTMLDivElement>(null);

    const animatedElementRef = useDynamicRef<HTMLElement | null>();
    const animatedElementResolverRefCallback = useCallback((domElementResolver: DomElementResolver | null) => {
        animatedElementRef.current = domElementResolver ?
            domElementResolver.getDomElement() as HTMLElement :
            null;
    }, [])


    const elementScrollHeight = useElementLayoutBinding(animatedElementRef, useCallback((element) => {
        return element.scrollHeight;
    }, []), referencesEqualityComparer)

    const prevScrollHeightRef = useRef<number | null>(null);
    useEffect(() => {
        prevScrollHeightRef.current = elementScrollHeight;
    }, [elementScrollHeight]);

    const currentAnimationRef = useRef<Animation>();

    const renderState = useRenderState({
        durationMilliseconds: durationMilliseconds
    })

    useLayoutEffect(() => {
        const elementHeight = prevScrollHeightRef.current;

        if (elementScrollHeight !== null && elementHeight !== null) {

            const wasAnimating = !(currentAnimationRef.current?.finished ?? true);

            currentAnimationRef.current?.cancel();

            currentAnimationRef.current = rootRef.current?.animate?.([
                {height: `${wasAnimating ? assertNotNullable(rootRef.current).offsetHeight : elementHeight}px`},
                {height: `${elementScrollHeight}px`},
            ], {
                duration: renderState.current.durationMilliseconds,
                iterations: 1
            })
        }

    }, [renderState, elementScrollHeight])

    return (
        <div
            ref={rootRef}
            {...htmlAttributes}
            className={style(classes.root, className)}
        >
            <DomElementResolver
                ref={animatedElementResolverRefCallback}
            >
                {children}
            </DomElementResolver>
        </div>
    );
});

export namespace AnimatedAutoSizedElement {

    export interface Props extends React.HTMLAttributes<any> {
        children: React.ReactElement;
        durationMilliseconds?: number;
    }
}
